// ruleFields.js — frontend MIRROR of the auto-tagging rule allowlist.
//
// The authoritative allowlist lives server-side in api/src/lib/ruleEngine.js
// (RULE_FIELDS / RULE_OPERATORS / COUNT_OPERATORS / AGGREGATE_FIELDS /
// ATTACHMENT_CATEGORY_KEYS) and is RE-validated on every save — this file only
// drives the builder's pickers. Keep the field/operator keys in sync (a
// cross-package test pins them); the server is the security boundary, not this
// list. WHICH fields a notification rule may use is further gated per rule
// kind server-side (notificationSchema.js) — fieldsForRuleKind() mirrors that
// gate for the wizard's pickers.

import { ATTACHMENT_CATEGORIES, FIELD_META } from '../lib/eventVocabularies.js';
import { COUNTRY_GROUPS } from '@shared/roleSchema.js';
import { OUTSTANDING_REQUIREMENT_KEYS } from '@shared/ruleEngine.js';

// STRING/taxonomy fields read from the CASE ROW (compared with string ops).
const STRING_FIELDS = [
  { key: 'treatment',        label: 'Treatment',         kind: 'string' },
  { key: 'treatmentSubtype', label: 'Treatment subtype', kind: 'string' },
  { key: 'severity',         label: 'Severity',          kind: 'string' },
  { key: 'status',           label: 'Status',            kind: 'string' },
  { key: 'eventType',        label: 'Event type',        kind: 'string' },
  { key: 'incidentCategory', label: 'Incident category', kind: 'string' },
  { key: 'clinic',           label: 'Clinic',            kind: 'string' },
  { key: 'region',           label: 'Region',            kind: 'string' },
  { key: 'country',          label: 'Country',           kind: 'string' },
];

// Ctx-derived STRING fields — values come from the dispatch/scan context, not
// the case row. previous* only carry values on change-trigger dispatches
// (status_changed / severity_changed); requirement* only on requirement-trigger
// dispatches (requirement_added / requirement_signed_off); otherwise they fail
// closed server-side.
const CONTEXT_STRING_FIELDS = [
  { key: 'ops',              label: 'OPS (IOI / GB / US)', kind: 'string' },
  { key: 'previousSeverity', label: 'Previous severity', kind: 'string' },
  { key: 'previousStatus',   label: 'Previous status',   kind: 'string' },
  { key: 'requirementKey',   label: 'Requirement type',  kind: 'string' },
  { key: 'requirementLabel', label: 'Requirement label', kind: 'string' },
];

// Derived NUMERIC (non-temporal) fields — severity 'Code N' → N, anything else
// ('Not AE', free text, none) → no match. Enables ordinal rules like "≥ 4".
const NUMERIC_FIELDS = [
  { key: 'severityLevel',         label: 'Severity level (1–5)',          kind: 'numeric' },
  { key: 'previousSeverityLevel', label: 'Previous severity level (1–5)', kind: 'numeric' },
];

// TEMPORAL fields — derived day-counts (compared with numeric ops).
const TEMPORAL_FIELDS = [
  { key: 'daysFromIncident',        label: 'Days since incident',     kind: 'numeric' },
  { key: 'daysFromReported',        label: 'Days since reported',     kind: 'numeric' },
  { key: 'daysInCurrentStatus',     label: 'Days in current status',  kind: 'numeric' },
  { key: 'daysUntilLegalThreshold', label: 'Days until legal limit',  kind: 'numeric' },
  { key: 'daysSinceLastActivity',   label: 'Days since last activity', kind: 'numeric' },
];

// The FULL leaf-field mirror (order matches the server's RULE_FIELDS).
export const RULE_FIELDS = [
  ...STRING_FIELDS,
  ...CONTEXT_STRING_FIELDS,
  ...NUMERIC_FIELDS,
  ...TEMPORAL_FIELDS,
];

// The pre-v2 TAG-rule picker list — RuleBuilder's DEFAULT `fields`, so the tag
// editor is unaffected by the notification-only fields. The extras would fail
// closed in tag evaluation anyway (no ops / previous / lastActivity in the tag
// ctx), so offering them there would only create silently-dead tag rules.
export const TAG_RULE_FIELDS = [
  ...STRING_FIELDS,
  ...TEMPORAL_FIELDS.filter(f => f.key !== 'daysSinceLastActivity'),
];

// field → kind map ('string' | 'numeric').
export const FIELD_KINDS = Object.fromEntries(RULE_FIELDS.map(f => [f.key, f.kind]));

const TEMPORAL_FIELD_KEYS = new Set(TEMPORAL_FIELDS.map(f => f.key));

/** True for the derived day-count fields (numeric AND measured in days). */
export const isTemporalField = (field) => TEMPORAL_FIELD_KEYS.has(field);

export const RULE_OPERATORS = [
  { key: 'equals',         label: 'equals',           multi: false, kind: 'string'  },
  { key: 'notEquals',      label: 'does not equal',   multi: false, kind: 'string'  },
  { key: 'in',             label: 'is any of',        multi: true,  kind: 'string'  },
  { key: 'notIn',          label: 'is none of',       multi: true,  kind: 'string'  },
  { key: 'greaterThan',    label: 'is more than',     multi: false, kind: 'numeric' },
  { key: 'greaterOrEqual', label: 'is at least',      multi: false, kind: 'numeric' },
  { key: 'lessThan',       label: 'is less than',     multi: false, kind: 'numeric' },
  { key: 'lessOrEqual',    label: 'is at most',       multi: false, kind: 'numeric' },
];

export const STRING_OPERATORS  = RULE_OPERATORS.filter(o => o.kind === 'string');
export const NUMERIC_OPERATORS = RULE_OPERATORS.filter(o => o.kind === 'numeric');

// Aggregate (windowed-count) condition pickers.
export const AGGREGATE_FIELDS = [
  { key: 'staffMemberConcerned',     label: 'Same staff member concerned' },
  { key: 'clinicManagerResponsible', label: 'Same clinic manager' },
  { key: 'medicalTeamMember',        label: 'Same medical team member' },
];

export const COUNT_OPERATORS = [
  { key: 'countEquals',         label: 'exactly' },
  { key: 'countGreaterOrEqual', label: 'at least' },
  { key: 'countGreaterThan',    label: 'more than' },
  { key: 'countLessThan',       label: 'fewer than' },
  { key: 'countLessOrEqual',    label: 'at most' },
];

// Window presets for the aggregate editor ('all' = ever).
export const AGGREGATE_WINDOWS = [
  { key: 30,    label: 'last 30 days' },
  { key: 90,    label: 'last 90 days' },
  { key: 180,   label: 'last 180 days' },
  { key: 365,   label: 'last 365 days' },
  { key: 'all', label: 'ever' },
];

// fileCount categories — a { key, label } picker over the wizard's typed upload
// slots. The canonical key list is pinned server-side (ATTACHMENT_CATEGORY_KEYS
// in ruleEngine.js); labels come from the existing frontend vocabulary.
export const FILE_COUNT_CATEGORIES = ATTACHMENT_CATEGORIES;

// outstandingRequirement key picker — { key, label } over the server's
// OUTSTANDING_REQUIREMENT_KEYS ('any' + the upload categories the matrix can
// require + the matrix-required type_specific field keys). Labels come from the
// existing vocabularies (attachment labels / FIELD_META). Per-case (admin-added)
// requirement type keys are config-driven and are matched by 'any' (the server
// also accepts them verbatim on a stored rule — see optionsWithStored below).
const ATTACHMENT_LABEL_BY_KEY = Object.fromEntries(ATTACHMENT_CATEGORIES.map(c => [c.key, c.label]));
export const OUTSTANDING_REQUIREMENT_OPTIONS = OUTSTANDING_REQUIREMENT_KEYS.map(key => ({
  key,
  label: key === 'any' ? 'Any outstanding requirement'
    : ATTACHMENT_LABEL_BY_KEY[key] ? `${ATTACHMENT_LABEL_BY_KEY[key]} (document)`
    : FIELD_META?.[key]?.label ? `${FIELD_META[key].label} (field)`
    : key,
}));
const OUTSTANDING_LABEL_BY_KEY = Object.fromEntries(OUTSTANDING_REQUIREMENT_OPTIONS.map(o => [o.key, o.label]));

export const RULE_FIELD_KEYS = RULE_FIELDS.map(f => f.key);
export const RULE_OP_KEYS    = RULE_OPERATORS.map(o => o.key);

export const isMultiOp   = (op)    => !!RULE_OPERATORS.find(o => o.key === op)?.multi;
export const isNumericOp = (op)    => RULE_OPERATORS.find(o => o.key === op)?.kind === 'numeric';
export const fieldKind   = (field) => FIELD_KINDS[field] || 'string';

/** Operators valid for a given field kind. */
export function operatorsForField(field) {
  return fieldKind(field) === 'numeric' ? NUMERIC_OPERATORS : STRING_OPERATORS;
}

// ── Per-rule-kind field filtering (notification wizard) ───────────────────────
// Mirrors the server's per-kind condition gate (notificationSchema.js):
//   base (both kinds)  — the 9 case string fields + ops + severityLevel.
//   EVENT rules        — base; previous* only when a change trigger
//                        (status_changed / severity_changed) is selected;
//                        requirement* only when a requirement trigger
//                        (requirement_added / requirement_signed_off) is selected.
//   SCHEDULED rules    — base + ALL temporal day-count fields; never previous*
//                        or requirement*.
// fileCount nodes are allowed for BOTH notification kinds (pass
// allowFileCount to RuleBuilder); aggregate nodes for NEITHER.

const CHANGE_TRIGGERS = ['status_changed', 'severity_changed'];
const REQUIREMENT_TRIGGERS = ['requirement_added', 'requirement_signed_off'];
const fieldByKey = Object.fromEntries(RULE_FIELDS.map(f => [f.key, f]));
const pickFields = (keys) => keys.map(k => fieldByKey[k]);

const BASE_NOTIFICATION_FIELDS = pickFields([
  ...STRING_FIELDS.map(f => f.key), 'ops', 'severityLevel',
]);
const PREVIOUS_NOTIFICATION_FIELDS = pickFields([
  'previousSeverity', 'previousStatus', 'previousSeverityLevel',
]);
const REQUIREMENT_NOTIFICATION_FIELDS = pickFields([
  'requirementKey', 'requirementLabel',
]);

/**
 * fieldsForRuleKind(kind, triggers) — the leaf fields a notification rule of
 * this kind may condition on, as { key, label, kind } descriptors for
 * RuleBuilder's `fields` prop. `triggers` is the rule's selected trigger list
 * (event rules only; ignored for scheduled).
 */
export function fieldsForRuleKind(kind, triggers = []) {
  if (kind === 'scheduled') return [...BASE_NOTIFICATION_FIELDS, ...TEMPORAL_FIELDS];
  const selected = Array.isArray(triggers) ? triggers : [];
  const fields = [...BASE_NOTIFICATION_FIELDS];
  if (selected.some(t => CHANGE_TRIGGERS.includes(t))) fields.push(...PREVIOUS_NOTIFICATION_FIELDS);
  if (selected.some(t => REQUIREMENT_TRIGGERS.includes(t))) fields.push(...REQUIREMENT_NOTIFICATION_FIELDS);
  return fields;
}

// ── Value suggestions for ctx-derived fields ──────────────────────────────────

/** The values the derived `ops` field can hold — one per country group
 *  (sourced from the shared COUNTRY_GROUPS so US et al. appear automatically). */
export const OPS_VALUES = [...COUNTRY_GROUPS];

// previous* string fields share the live vocabularies of their base fields.
const FIELD_VALUE_ALIASES = { previousSeverity: 'severity', previousStatus: 'status' };

/**
 * suggestionsForField(field, fieldValues) — the known-value dropdown list for a
 * field: the caller-supplied fieldValues entry, its alias (previousSeverity →
 * severity, previousStatus → status), or the fixed OPS pair. [] = free text.
 */
export function suggestionsForField(field, fieldValues = {}) {
  const direct = fieldValues?.[field];
  if (Array.isArray(direct) && direct.length) return direct;
  const alias = FIELD_VALUE_ALIASES[field];
  if (alias && Array.isArray(fieldValues?.[alias])) return fieldValues[alias];
  if (field === 'ops') return OPS_VALUES;
  return [];
}

/** A fresh, empty top-level rule group. */
export function emptyRule() {
  return { enabled: true, match: 'all', rules: [] };
}

/** A fresh condition (first field, equals). */
export function emptyCondition() {
  return { field: RULE_FIELD_KEYS[0], op: 'equals', value: '' };
}

/** A fresh aggregate condition (windowed people-count). */
export function emptyAggregate() {
  return { type: 'aggregate', field: AGGREGATE_FIELDS[0].key, window: 90, op: 'countGreaterOrEqual', value: 2 };
}

/** A fresh fileCount condition (this case's attachments in one category slot). */
export function emptyFileCount() {
  return { type: 'fileCount', category: FILE_COUNT_CATEGORIES[0].key, op: 'countGreaterOrEqual', value: 1 };
}

/** A fresh outstandingRequirement condition (this case still has a required item missing). */
export function emptyOutstandingRequirement() {
  return { type: 'outstandingRequirement', key: 'any', minDays: 0 };
}

/** A fresh nested group. */
export function emptyGroup() {
  return { match: 'all', rules: [] };
}

export const isAggregate = (node) => node && typeof node === 'object' && node.type === 'aggregate';
export const isFileCount = (node) => node && typeof node === 'object' && node.type === 'fileCount';
export const isOutstandingRequirement = (node) =>
  node && typeof node === 'object' && node.type === 'outstandingRequirement';
export const isGroup = (node) =>
  node && typeof node === 'object'
  && !isAggregate(node) && !isFileCount(node) && !isOutstandingRequirement(node)
  && Array.isArray(node.rules);

/**
 * Build a default condition shaped for a field's kind (numeric fields default to
 * a numeric op + numeric value; string fields to equals + '').
 */
export function conditionForField(field) {
  if (fieldKind(field) === 'numeric') {
    return { field, op: 'greaterThan', value: 0 };
  }
  return { field, op: 'equals', value: '' };
}

// ── Multi-value (in/notIn) chip helpers ───────────────────────────────────────

/** Split raw input on commas; trim and drop empties. */
export function parseMultiValueInput(raw) {
  return String(raw ?? '').split(',').map(s => s.trim()).filter(Boolean);
}

/** Add one value to a multi-value list (case-insensitive dedup). */
export function addMultiValue(values, raw) {
  const v = String(raw ?? '').trim();
  if (!v) return values ?? [];
  const list = Array.isArray(values) ? values.slice() : [];
  if (list.some(x => String(x).trim().toLowerCase() === v.toLowerCase())) return list;
  return [...list, v];
}

/** Remove value at index from a multi-value list. */
export function removeMultiValue(values, index) {
  const list = Array.isArray(values) ? values.slice() : [];
  if (index < 0 || index >= list.length) return list;
  list.splice(index, 1);
  return list;
}

// ── Readable summary labels ────────────────────────────────────────────────────

const FIELD_LABEL = Object.fromEntries(RULE_FIELDS.map(f => [f.key, f.label]));
const OP_LABEL    = Object.fromEntries(RULE_OPERATORS.map(o => [o.key, o.label]));
const AGG_FIELD_LABEL = Object.fromEntries(AGGREGATE_FIELDS.map(f => [f.key, f.label]));
const FILE_CAT_LABEL  = Object.fromEntries(FILE_COUNT_CATEGORIES.map(c => [c.key, c.label]));

const COUNT_OP_SYMBOL = {
  countEquals: 'exactly', countGreaterOrEqual: '≥', countGreaterThan: '>',
  countLessThan: '<', countLessOrEqual: '≤',
};

/** Human one-liner for a leaf/aggregate/fileCount node (e.g. "Days since reported is more than 90"). */
export function describeNode(node) {
  if (isAggregate(node)) {
    const cnt = COUNT_OP_SYMBOL[node.op] || node.op;
    const win = node.window === 'all' ? 'ever' : `in last ${node.window} days`;
    return `${AGG_FIELD_LABEL[node.field] || node.field} on ${cnt} ${node.value} cases ${win}`;
  }
  if (isFileCount(node)) {
    const cnt = COUNT_OP_SYMBOL[node.op] || node.op;
    return `${FILE_CAT_LABEL[node.category] || node.category} files ${cnt} ${node.value}`;
  }
  if (isOutstandingRequirement(node)) {
    const what = node.key === 'any' || node.key == null
      ? 'Any required item'
      : (OUTSTANDING_LABEL_BY_KEY[node.key] || node.key);
    const days = Number.isInteger(node.minDays) && node.minDays > 0
      ? ` for at least ${node.minDays} day${node.minDays === 1 ? '' : 's'}`
      : '';
    return `${what} outstanding${days}`;
  }
  const f = FIELD_LABEL[node?.field] || node?.field;
  const o = OP_LABEL[node?.op] || node?.op;
  const v = Array.isArray(node?.value) ? node.value.join(', ') : node?.value;
  return `${f} ${o} ${v}`;
}

// ── Preview gate ───────────────────────────────────────────────────────────────
// Whether a rule is worth sending to the live match-count preview endpoint.
// Mirrors the server's intent so a HALF-TYPED rule never flickers a 400 /
// "Preview failed": we only call when the rule is ENABLED and EVERY node is
// complete. Pure + unit-tested; the server still re-validates.

/** True when a single LEAF condition has a usable value for its operator. */
function conditionHasValue(cond) {
  if (!cond || typeof cond !== 'object') return false;
  if (!RULE_FIELD_KEYS.includes(cond.field) || !RULE_OP_KEYS.includes(cond.op)) return false;
  if (isMultiOp(cond.op)) {
    return Array.isArray(cond.value) && cond.value.some(v => String(v ?? '').trim() !== '');
  }
  if (isNumericOp(cond.op)) {
    return typeof cond.value === 'number' && Number.isFinite(cond.value);
  }
  return String(cond.value ?? '').trim() !== '';
}

/** True when an AGGREGATE node is complete (allowlisted field/op, bounded value/window). */
function aggregateIsComplete(node) {
  if (!isAggregate(node)) return false;
  if (!AGGREGATE_FIELDS.some(f => f.key === node.field)) return false;
  if (!COUNT_OPERATORS.some(o => o.key === node.op)) return false;
  if (typeof node.value !== 'number' || !Number.isInteger(node.value) || node.value < 0) return false;
  if (node.window !== 'all' && (!Number.isInteger(node.window) || node.window <= 0)) return false;
  return true;
}

/** True when a FILECOUNT node is complete (known category/op, non-negative int value). */
function fileCountIsComplete(node) {
  if (!isFileCount(node)) return false;
  if (!FILE_COUNT_CATEGORIES.some(c => c.key === node.category)) return false;
  if (!COUNT_OPERATORS.some(o => o.key === node.op)) return false;
  return typeof node.value === 'number' && Number.isInteger(node.value) && node.value >= 0;
}

/** True when an OUTSTANDINGREQUIREMENT node is complete (non-empty key, optional non-negative int minDays).
 *  Mirrors the server's validateOutstandingRequirement shape check. */
export function outstandingRequirementIsComplete(node) {
  if (!isOutstandingRequirement(node)) return false;
  const key = String(node.key ?? '').trim();
  if (!key || !/^[a-z0-9][a-z0-9_:.-]{0,59}$/i.test(key)) return false;
  if (node.minDays == null) return true;
  return typeof node.minDays === 'number' && Number.isInteger(node.minDays) && node.minDays >= 0;
}

/** True when a group (recursively) is non-empty and every node is complete. */
function groupIsComplete(group) {
  if (!isGroup(group)) return false;
  const children = group.rules;
  if (!Array.isArray(children) || children.length === 0) return false;
  return children.every(child => {
    if (isAggregate(child)) return aggregateIsComplete(child);
    if (isFileCount(child)) return fileCountIsComplete(child);
    if (isOutstandingRequirement(child)) return outstandingRequirementIsComplete(child);
    if (isGroup(child))     return groupIsComplete(child);
    return conditionHasValue(child);
  });
}

/**
 * ruleIsPreviewable(rule) — gate the live preview call CLIENT-side. Only
 * previewable when the rule is enabled AND every node (leaf, aggregate,
 * fileCount, or nested group, at any depth) is complete.
 */
export function ruleIsPreviewable(rule) {
  if (!isGroup(rule)) return false;
  if (rule.enabled === false) return false;
  return groupIsComplete(rule);
}
